import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Mail,
  Lock,
  MonitorCheck,
  Pause,
  PencilLine,
  Play,
  MessagesSquare,
  Plus,
  Radio,
  Sparkles,
  Zap,
  Trash2,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatDateTime } from "@/utils/formatDate";
import { AronMarkdown } from "../components/AronMarkdown";
import { AronTaskEditor } from "../components/AronTaskEditor";
import { AronTaskTimeline, taskColor } from "../components/AronTaskTimeline";
import { useAronWorkModes } from "../api/aronApi";
import {
  useApproveAronScheduledTask,
  useAronPlannerOptions,
  useAronScheduledTaskRuns,
  useAronScheduledTasks,
  useAronUpcomingRuns,
  useDeleteAronScheduledTask,
  useMarkAronScheduledTaskRead,
  useRejectAronScheduledTask,
  useRunAronScheduledTaskNow,
  useUpdateAronScheduledTask,
} from "../api/aronTasksApi";
import type {
  AronScheduledTask,
  AronScheduledTaskRun,
  AronScheduledTaskRunMode,
  AronScheduledTaskRunStatus,
} from "../types";

const PANEL = "border border-[var(--color-border)] bg-[var(--color-bg-surface)]";
const LABEL =
  "font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-[var(--color-text-muted)]";
const CHIP =
  "border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)]";
const ICON_BTN =
  "border border-[var(--color-border)] p-1.5 text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]";

const RUN_BADGE: Record<AronScheduledTaskRunStatus, string> = {
  ok: "border-[#0f7b46] text-[#0f7b46]",
  running: "border-[#1f6fdc] text-[#1f6fdc]",
  error: "border-[var(--color-primary)] text-[var(--color-primary)]",
  skipped_budget: "border-[#a16207] text-[#a16207]",
  report_failed: "border-[#c2410c] text-[#c2410c]",
};

function fmtDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtShort(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${d.toLocaleDateString("pl-PL", { weekday: "short" })} ${formatDateTime(d)}`;
}

function Badge({ cls, children }: { cls: string; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${cls}`}
    >
      {children}
    </span>
  );
}

function StateBadge({ task }: { task: AronScheduledTask }) {
  if (task.approval_status === "pending_ceo") {
    return <Badge cls="border-[#a16207] text-[#a16207]">Czeka na CEO</Badge>;
  }
  if (task.approval_status === "rejected") {
    return (
      <Badge cls="border-[var(--color-primary)] text-[var(--color-primary)]">
        Odrzucone{task.rejection_reason ? `: ${task.rejection_reason}` : ""}
      </Badge>
    );
  }
  if (!task.is_active) {
    return (
      <Badge cls="border-[var(--color-border-strong)] text-[var(--color-text-muted)]">
        {task.paused_reason === "auto_failures" ? "Auto-pauza po błędach" : "Wstrzymane"}
      </Badge>
    );
  }
  return null;
}

const RUN_MODE_STYLE: Record<
  AronScheduledTaskRunMode,
  { icon: typeof Lock; cls: string }
> = {
  read_only: {
    icon: Lock,
    cls: "border-[var(--color-border)] text-[var(--color-text-muted)]",
  },
  proposals: {
    icon: PencilLine,
    cls: "border-[var(--color-border-strong)] text-[var(--color-text-secondary)]",
  },
  autopilot: {
    icon: Zap,
    cls: "border-[var(--color-primary)] text-[var(--color-primary)]",
  },
};

function RunModeBadge({ task }: { task: AronScheduledTask }) {
  const style = RUN_MODE_STYLE[task.run_mode] ?? RUN_MODE_STYLE.proposals;
  const Icon = style.icon;
  return (
    <Badge cls={style.cls}>
      <span className="inline-flex items-center gap-1">
        <Icon size={11} /> {task.run_mode_display}
        {task.write_scope.length > 0 && ` +${task.write_scope.length} z czatu`}
      </span>
    </Badge>
  );
}

function RunHistory({ task }: { task: AronScheduledTask }) {
  const { data: runs, isLoading } = useAronScheduledTaskRuns(task.id);
  const [openRun, setOpenRun] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-[11px] text-[var(--color-text-muted)]">
        <Loader2 size={12} className="animate-spin" /> Wczytuję przebiegi…
      </div>
    );
  }
  if (!runs?.length) {
    return (
      <div className="py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
        Brak przebiegów
      </div>
    );
  }

  const total = runs.reduce((sum, r) => sum + Number(r.cost_usd || 0), 0);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
        <span>{runs.length} przebiegów</span>
        <span>błędy: {runs.filter((r) => r.status === "error").length}</span>
        <span className="tabular-nums">koszt łącznie: {total.toFixed(4)} $</span>
      </div>
      {runs.map((run: AronScheduledTaskRun) => (
        <div key={run.id} className="border border-[var(--color-border-muted)] p-2">
          <button
            type="button"
            onClick={() => setOpenRun(openRun === run.id ? null : run.id)}
            className="flex w-full items-center justify-between gap-2 text-left"
          >
            <span className="flex items-center gap-2">
              {openRun === run.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              <Badge cls={RUN_BADGE[run.status]}>{run.status_display}</Badge>
              {run.manual && (
                <span className="font-mono text-[10px] text-[var(--color-text-muted)]">
                  ręczny
                </span>
              )}
              <span className="text-[11px] text-[var(--color-text-secondary)]">
                {fmtDate(run.created_at)}
              </span>
            </span>
            <span className="flex items-center gap-2 font-mono text-[10px] tabular-nums text-[var(--color-text-muted)]">
              <span>
                {run.auto_writes_count > 0 && `zapisy auto: ${run.auto_writes_count} · `}
                {run.pending_writes_count > 0 && `do zatwierdzenia: ${run.pending_writes_count} · `}
                {Number(run.cost_usd).toFixed(4)} $
              </span>
              {task.is_owner && run.conversation && (
                <Link
                  to={`/aron?c=${run.conversation}`}
                  onClick={(e) => e.stopPropagation()}
                  title={
                    run.status === "running"
                      ? "Podgląd na żywo"
                      : "Zapis rozmowy tego przebiegu"
                  }
                  className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                >
                  {run.status === "running" ? (
                    <Radio size={11} className="animate-pulse" />
                  ) : (
                    <MessagesSquare size={11} />
                  )}
                  {run.status === "running" ? "na żywo" : "zapis"}
                </Link>
              )}
            </span>
          </button>
          {openRun === run.id && (
            <div className="mt-2 space-y-2 border-t border-[var(--color-border-muted)] pt-2">
              {run.error && (
                <div className="text-[11px] text-[var(--color-primary)]">{run.error}</div>
              )}
              {run.report_text ? (
                <div className="max-h-72 overflow-y-auto border border-[var(--color-border-muted)] bg-[var(--color-bg-base)] p-3">
                  <AronMarkdown text={run.report_text} />
                </div>
              ) : (
                <div className="text-[11px] text-[var(--color-text-muted)]">
                  Brak treści raportu.
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] text-[var(--color-text-muted)]">
                {run.report_sent_at && (
                  <span className="inline-flex items-center gap-1">
                    <Mail size={11} /> wysłano: {run.report_recipients.join(", ")}
                  </span>
                )}
                {task.is_owner && run.conversation && (
                  <Link
                    to={`/aron?c=${run.conversation}`}
                    className="text-[var(--color-primary)] hover:underline"
                  >
                    otwórz całą rozmowę przebiegu →
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ModePicker({ task, canManage }: { task: AronScheduledTask; canManage: boolean }) {
  const update = useUpdateAronScheduledTask();
  const { data: workModes } = useAronWorkModes();
  const info = task.mode_info;
  const text = [info.label, info.model, info.detail].filter(Boolean).join(" · ");

  const short = [info.label, info.model].filter(Boolean).join(" · ") || text;

  if (!canManage || !workModes?.reasoning_modes?.length) {
    return (
      <Badge cls="border-[var(--color-border)] text-[var(--color-text-secondary)]">
        {short}
      </Badge>
    );
  }
  return (
    <select
      value={task.reasoning_mode}
      disabled={update.isPending}
      title={`${text} — zmiana obowiązuje od następnego przebiegu`}
      onChange={(e) =>
        update.mutate({
          id: task.id,
          patch: { reasoning_mode: e.target.value as AronScheduledTask["reasoning_mode"] },
        })
      }
      className="max-w-[22ch] truncate border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] hover:border-[var(--color-primary)]"
    >
      {workModes.reasoning_modes.map((m) => (
        <option key={m.value} value={m.value} title={`${m.detail} (${m.cost})`}>
          {m.label} · {m.model}
        </option>
      ))}
      {/* Mode currently flag-disabled but frozen on the task — keep it selectable-as-is. */}
      {!workModes.reasoning_modes.some((m) => m.value === task.reasoning_mode) && (
        <option value={task.reasoning_mode}>{short}</option>
      )}
    </select>
  );
}

function TaskCard({
  task,
  color,
  isCeo,
  selected,
  onSelect,
  onEdit,
}: {
  task: AronScheduledTask;
  color: string;
  isCeo: boolean;
  selected: boolean;
  onSelect: () => void;
  onEdit: () => void;
}) {
  const update = useUpdateAronScheduledTask();
  const remove = useDeleteAronScheduledTask();
  const runNow = useRunAronScheduledTaskNow();
  const approve = useApproveAronScheduledTask();
  const reject = useRejectAronScheduledTask();
  const markRead = useMarkAronScheduledTaskRead();
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [rejectReason, setRejectReason] = useState<string | null>(null);

  const canManage = task.can_manage;
  const pendingCeo = task.approval_status === "pending_ceo";

  return (
    <div
      className={`${PANEL} relative pl-3 ${selected ? "border-[var(--color-text-primary)]" : ""}`}
    >
      <span
        className="absolute top-0 bottom-0 left-0 w-[3px]"
        style={{ backgroundColor: color, opacity: task.next_runs.length ? 1 : 0.3 }}
      />
      <div className="flex flex-wrap items-start justify-between gap-3 p-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={onSelect}
              className="text-sm font-bold text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
              title="Pokaż na osi"
            >
              {task.name}
            </button>
            <Badge cls="border-[var(--color-border)] text-[var(--color-text-secondary)]">
              {task.cron_human}
            </Badge>
            <ModePicker task={task} canManage={canManage} />
            {task.visibility === "shared" && (
              <Badge cls="border-[#7b2fbe] text-[#7b2fbe]">
                Współdzielone: {task.audience_human}
              </Badge>
            )}
            <RunModeBadge task={task} />
            <StateBadge task={task} />
          </div>

          <p
            className="mt-1.5 line-clamp-2 text-[12px] text-[var(--color-text-secondary)]"
            title={task.instruction}
          >
            {task.instruction}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] text-[var(--color-text-muted)]">
            {task.owner_name && !task.is_owner && <span>właściciel: {task.owner_name}</span>}
            <span className="uppercase tracking-[0.14em]">
              następne:{" "}
              {task.next_runs.length
                ? task.next_runs.slice(0, 3).map(fmtShort).join(" · ")
                : "—"}
            </span>
            {task.email_to.length > 0 ? (
              <span className="inline-flex items-center gap-1">
                <Mail size={10} /> {task.email_to.join(", ")}
                {task.email_cc.length > 0 && ` (DW: ${task.email_cc.join(", ")})`}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1">
                <MonitorCheck size={10} /> wynik tylko na tej stronie
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {task.unread_runs > 0 && (
            <span className="flex items-stretch">
              <button
                type="button"
                title="Pokaż wynik (i oznacz jako odczytany)"
                disabled={markRead.isPending}
                onClick={() => {
                  setExpanded(true);
                  markRead.mutate(task.id);
                }}
                className="flex items-center gap-1 bg-[var(--color-primary)] px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white hover:bg-[var(--color-primary-hover)]"
              >
                {markRead.isPending ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Sparkles size={11} />
                )}
                nowy wynik{task.unread_runs > 1 ? ` ×${task.unread_runs}` : ""}
              </button>
              <button
                type="button"
                title="Odznacz — wiem o tym wyniku"
                disabled={markRead.isPending}
                onClick={() => markRead.mutate(task.id)}
                className="border-l border-white/40 bg-[var(--color-primary)] px-1.5 text-white hover:bg-[var(--color-primary-hover)]"
              >
                <Check size={12} />
              </button>
            </span>
          )}
          {task.last_run ? (
            <span className="flex flex-col items-end gap-0.5">
              {task.last_run.status === "running" &&
              task.last_run.conversation &&
              task.is_owner ? (
                <Link
                  to={`/aron?c=${task.last_run.conversation}`}
                  title="Podejrzyj przebieg na żywo — zobaczysz, co ARON właśnie robi"
                  className="flex items-center gap-1 border border-[#1f6fdc] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#1f6fdc] hover:bg-[#1f6fdc] hover:text-white"
                >
                  <Radio size={11} className="animate-pulse" /> w toku — podgląd
                </Link>
              ) : (
                <Badge cls={RUN_BADGE[task.last_run.status]}>{task.last_run.status_display}</Badge>
              )}
              <span className="flex items-center gap-2 font-mono text-[10px] text-[var(--color-text-muted)]">
                {fmtDate(task.last_run.finished_at)}
                {task.is_owner &&
                  task.last_run.conversation &&
                  task.last_run.status !== "running" && (
                  <Link
                    to={`/aron?c=${task.last_run.conversation}`}
                    title="Otwórz zapis rozmowy tego przebiegu"
                    className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                  >
                    <MessagesSquare size={11} /> zapis
                  </Link>
                )}
              </span>
            </span>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
              brak przebiegów
            </span>
          )}

          {canManage && task.approval_status !== "rejected" && (
            <button
              type="button"
              title={task.is_active ? "Wstrzymaj" : "Wznów"}
              disabled={update.isPending}
              onClick={() => update.mutate({ id: task.id, patch: { is_active: !task.is_active } })}
              className={`${ICON_BTN} ${
                task.is_active ? "" : "border-[var(--color-primary)] text-[var(--color-primary)]"
              }`}
            >
              {update.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : task.is_active ? (
                <Pause size={13} />
              ) : (
                <Play size={13} />
              )}
            </button>
          )}
          {canManage && !pendingCeo && task.approval_status !== "rejected" && (
            <button
              type="button"
              title="Uruchom teraz"
              disabled={runNow.isPending}
              onClick={() => runNow.mutate(task.id)}
              className={ICON_BTN}
            >
              {runNow.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Play size={13} />
              )}
            </button>
          )}
          {canManage && (
            <button type="button" title="Edytuj zadanie" onClick={onEdit} className={ICON_BTN}>
              <PencilLine size={13} />
            </button>
          )}
          {canManage &&
            (confirmDelete ? (
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  className="bg-[var(--color-primary)] px-1.5 py-1 font-mono text-[10px] uppercase text-white"
                  onClick={() => remove.mutate(task.id)}
                >
                  usuń
                </button>
                <button
                  type="button"
                  className="border border-[var(--color-border)] px-1.5 py-1 font-mono text-[10px] uppercase text-[var(--color-text-muted)]"
                  onClick={() => setConfirmDelete(false)}
                >
                  nie
                </button>
              </span>
            ) : (
              <button
                type="button"
                title="Usuń zadanie"
                onClick={() => setConfirmDelete(true)}
                className={ICON_BTN}
              >
                <Trash2 size={13} />
              </button>
            ))}
          <button
            type="button"
            title="Historia przebiegów"
            onClick={() => setExpanded(!expanded)}
            className={ICON_BTN}
          >
            {expanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
          </button>
        </div>
      </div>

      {isCeo && pendingCeo && (
        <div className="mx-3 mb-3 flex flex-wrap items-center gap-2 border border-[#a16207] p-2">
          <span className="text-[11px] text-[var(--color-text-primary)]">
            Zadanie współdzielone czeka na Twoją decyzję:
          </span>
          <button
            type="button"
            disabled={approve.isPending}
            onClick={() => approve.mutate(task.id)}
            className="flex items-center gap-1 bg-[#0f7b46] px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-white"
          >
            <Check size={11} /> Akceptuj
          </button>
          {rejectReason === null ? (
            <button
              type="button"
              onClick={() => setRejectReason("")}
              className="flex items-center gap-1 border border-[var(--color-primary)] px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-[var(--color-primary)]"
            >
              <X size={11} /> Odrzuć
            </button>
          ) : (
            <span className="flex items-center gap-1">
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Powód odrzucenia"
                className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1 text-[11px]"
              />
              <button
                type="button"
                disabled={reject.isPending}
                onClick={() => reject.mutate({ id: task.id, reason: rejectReason })}
                className="bg-[var(--color-primary)] px-2 py-1 font-mono text-[10px] uppercase text-white"
              >
                Odrzuć
              </button>
            </span>
          )}
        </div>
      )}

      {expanded && (
        <div className="border-t border-[var(--color-border-muted)] p-3">
          <RunHistory task={task} />
        </div>
      )}
    </div>
  );
}

export function AronScheduledTasksPage() {
  const user = useAuthStore((s) => s.user);
  const { data: tasks, isLoading, isError } = useAronScheduledTasks();
  const { data: options } = useAronPlannerOptions();
  const [days, setDays] = useState(14);
  const { data: timeline, isFetching: timelineLoading } = useAronUpcomingRuns(days);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AronScheduledTask | null>(null);
  const isCeo = !!user?.is_ceo;

  const colorOf = useMemo(() => {
    const map = new Map<string, string>();
    (tasks ?? []).forEach((t, i) => map.set(t.id, taskColor(i)));
    return map;
  }, [tasks]);

  const openEditor = (task: AronScheduledTask | null) => {
    setEditing(task);
    setEditorOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-[var(--color-text-muted)]">
        <Loader2 size={16} className="animate-spin" /> Wczytuję zadania cykliczne…
      </div>
    );
  }
  if (isError || !tasks) {
    return (
      <div className="p-6 text-sm text-[var(--color-primary)]">
        Nie udało się wczytać zadań cyklicznych.
      </div>
    );
  }

  const mine = tasks.filter((t) => t.is_owner);
  const shared = tasks.filter((t) => !t.is_owner);
  const active = tasks.filter((t) => t.is_active && t.approval_status === "approved").length;
  const paused = tasks.filter((t) => !t.is_active).length;
  // ISO strings sort chronologically — the earliest first fire time across tasks.
  const nextRun = tasks.flatMap((t) => t.next_runs.slice(0, 1)).sort()[0];

  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center bg-[var(--color-primary)] text-white">
            <CalendarClock size={16} />
          </span>
          <div>
            <h1 className="font-mono text-base font-bold uppercase tracking-[0.12em] text-[var(--color-text-primary)]">
              Planner ARON
            </h1>
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Zadania wykonywane wg harmonogramu — raport idzie e-mailem. Definiujesz je tutaj
              albo w rozmowie z ARONem.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex flex-col items-end">
            <span className={LABEL}>Aktywne / wstrzymane</span>
            <span className="font-mono text-sm tabular-nums text-[var(--color-text-primary)]">
              {active} / {paused}
            </span>
          </span>
          <span className="flex flex-col items-end">
            <span className={LABEL}>Najbliższe uruchomienie</span>
            <span className="font-mono text-sm tabular-nums text-[var(--color-text-primary)]">
              {nextRun ? fmtShort(nextRun) : "—"}
            </span>
          </span>
          <button
            type="button"
            onClick={() => openEditor(null)}
            className="flex items-center gap-1.5 bg-[var(--color-primary)] px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-wider text-white hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={13} /> Nowe zadanie
          </button>
        </div>
      </header>

      {options && !options.scheduled_tasks_enabled && (
        <p className="flex items-start gap-2 border border-[var(--color-primary)] bg-[var(--color-primary-dim)] px-3 py-2 text-[12px] text-[var(--color-text-primary)]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-[var(--color-primary)]" />
          Automat wykonujący zadania cykliczne jest wyłączony na tym środowisku — nic nie
          odpali się samo. Zadania możesz definiować i uruchamiać ręcznie.
        </p>
      )}

      <AronTaskTimeline
        tasks={tasks}
        timeline={timeline}
        isLoading={timelineLoading}
        days={days}
        onDaysChange={setDays}
        selectedTaskId={selectedTaskId}
        onSelectTask={setSelectedTaskId}
      />

      <section className="space-y-2">
        <h2 className={LABEL}>Moje zadania</h2>
        {mine.length === 0 ? (
          <div className={`${PANEL} border-dashed p-8 text-center`}>
            <Bot size={20} className="mx-auto mb-2 text-[var(--color-text-muted)]" />
            <p className="text-[12px] text-[var(--color-text-secondary)]">
              Nie masz jeszcze zadań cyklicznych.
            </p>
            <button
              type="button"
              onClick={() => openEditor(null)}
              className="mt-3 inline-flex items-center gap-1.5 border border-[var(--color-primary)] px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white"
            >
              <Plus size={12} /> Zdefiniuj pierwsze zadanie
            </button>
            <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
              Albo poproś ARONa na czacie: „w każdy piątek o 16 sprawdź X i wyślij mi raport”.
            </p>
          </div>
        ) : (
          mine.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              color={colorOf.get(t.id) ?? "#dc1f1f"}
              isCeo={isCeo}
              selected={selectedTaskId === t.id}
              onSelect={() => setSelectedTaskId(selectedTaskId === t.id ? null : t.id)}
              onEdit={() => openEditor(t)}
            />
          ))
        )}
      </section>

      {shared.length > 0 && (
        <section className="space-y-2">
          <h2 className={LABEL}>Współdzielone</h2>
          {shared.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              color={colorOf.get(t.id) ?? "#dc1f1f"}
              isCeo={isCeo}
              selected={selectedTaskId === t.id}
              onSelect={() => setSelectedTaskId(selectedTaskId === t.id ? null : t.id)}
              onEdit={() => openEditor(t)}
            />
          ))}
        </section>
      )}

      <p className={`${CHIP} inline-flex border-none px-0`}>
        Strefa czasowa: {options?.timezone ?? "Europe/Warsaw"}
      </p>

      <AronTaskEditor
        open={editorOpen}
        task={editing}
        options={options}
        onClose={() => setEditorOpen(false)}
      />
    </div>
  );
}
